import { CaretUpDown, GearSix, PlusCircle, Stack } from "@phosphor-icons/react";
import { clsx } from "clsx";
import { useState } from "react";
import { Link } from "react-router-dom";

import { CreateWorkspaceModal } from "common/components/CreateWorkspace";
import { MenuSearchInput } from "common/components/MenuSearchInput";
import RelativeLink from "common/components/RelativeLink";
import RenderIf from "common/components/RenderIf";
import { dropdownMenuItemVariants } from "common/components/ui/DropdownMenu";
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from "common/components/ui/Popover";
import WorkspaceImage from "common/components/WorkspaceImage";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useInvitations from "common/datahooks/useInvitations";
import useWorkspaces from "common/datahooks/useWorkspaces";
import { getWhitelabelCustomization } from "common/helpers/utils";

import InvitationItem from "./InvitationItem";
import WorkspaceItem from "./WorkspaceItem";

const scrollThreshold = 5;
export default function DesktopWorkspaceSelector({
  isExpanded,
}: {
  isExpanded: boolean;
}) {
  const { id: workspaceId, icon_url: workspaceImage } =
    useSelectedWorkspaceContext();
  const [isCreateWorkspaceOpen, setIsCreateWorkspaceOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const { invitations } = useInvitations();
  const { workspaces } = useWorkspaces();

  const { name: workspaceName } = workspaces.find(
    ({ id }) => id === workspaceId,
  );

  const lowercasedInput = inputValue.toLowerCase();

  const filteredWorkspaces = workspaces.filter(({ name }) =>
    name.toLowerCase().includes(lowercasedInput),
  );
  const filteredInvitations = invitations
    ? invitations.filter(({ workspace_name }) =>
        workspace_name.toLowerCase().includes(lowercasedInput),
      )
    : [];

  const workspacesCount = workspaces.length;
  const invitationsCount = invitations ? invitations.length : 0;

  const isOverScrollThreshold =
    workspacesCount + invitationsCount > scrollThreshold;

  const isEmptySearch =
    !!inputValue && !filteredWorkspaces.length && !filteredInvitations.length;

  return (
    <>
      <Popover>
        <PopoverTrigger
          className={clsx(
            "flex max-w-full gap-x-2 flex-grow items-center rounded-2xl hover:bg-black-100 hover:border-black-200 border h-9 border-transparent transition-[background,border,boxShadow] data-[state=open]:hover:bg-white data-[state=open]:hover:border-purple-400 data-[state=open]:bg-white data-[state=open]:shadow-md data-[state=open]:border-purple-400",
            isExpanded
              ? "data-[state=open]:bg-white pl-0.75 pr-0.5"
              : "px-0.75",
          )}
        >
          <WorkspaceImage
            id={workspaceId}
            size="xs"
            imageSrc={workspaceImage}
          />

          <div
            className={clsx(
              "overflow-hidden flex transition-opacity flex-1 gap-x-1.5",
              isExpanded ? "opacity-100" : "opacity-0",
            )}
          >
            <span className="line-clamp-1 break-all text-left text-xs-bold">
              {workspaceName}
            </span>
            <CaretUpDown size={16} className="ml-auto text-black-500" />
          </div>
        </PopoverTrigger>

        <PopoverContent
          className="flex w-72 flex-col"
          align="start"
          side="right"
        >
          {/* Heading */}
          <RenderIf condition={isOverScrollThreshold}>
            <MenuSearchInput
              placeholder="Search workspaces"
              value={inputValue}
              sizeVariant="sm"
              onChange={(e) => setInputValue(e.target.value)}
            />
          </RenderIf>
          <div
            className={clsx(
              "flex flex-col gap-y-1 overflow-y-auto scrollbar-thin px-2 py-1",
              isOverScrollThreshold ? "max-h-71" : "",
            )}
          >
            {isEmptySearch ? (
              <p className="text-center text-black-500 py-20 my-auto">
                No results found for{" "}
                <span className="text-black-950">{inputValue}</span>
              </p>
            ) : (
              <>
                {filteredInvitations.map((invitation) => (
                  <InvitationItem
                    key={invitation.token}
                    invitation={invitation}
                  />
                ))}

                {filteredWorkspaces.map((workspace) => (
                  <WorkspaceItem key={workspace.id} workspace={workspace} />
                ))}
              </>
            )}
          </div>

          <RenderIf condition={!getWhitelabelCustomization()}>
            <div className="px-1 pt-1 flex flex-col">
              <PopoverClose asChild>
                <button
                  type="button"
                  className={dropdownMenuItemVariants({ size: "sm" })}
                  onClick={() => setIsCreateWorkspaceOpen(true)}
                >
                  <PlusCircle weight="fill" /> New workspace
                </button>
              </PopoverClose>
              <RelativeLink
                to="settings/general"
                className={dropdownMenuItemVariants({ size: "sm" })}
              >
                <GearSix weight="fill" /> Workspace settings
              </RelativeLink>
              <Link
                to="/workspaces"
                className={dropdownMenuItemVariants({ size: "sm" })}
              >
                <Stack weight="fill" /> View all workspaces
              </Link>
            </div>
          </RenderIf>
        </PopoverContent>
      </Popover>
      <CreateWorkspaceModal
        isOpen={isCreateWorkspaceOpen}
        setIsOpen={setIsCreateWorkspaceOpen}
      />
    </>
  );
}
